import React from "react";
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import PublicIcon from "@mui/icons-material/Public";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import { ExampleLines, formatIPA, playPronunciation } from "../flashcardUtils";

function DeckReviewPanel({
  selectedDeck,
  activeCard,
  cards,
  progress,
  isFlipped,
  isMineDeck,
  isSignedIn,
  hasNextCard,
  onFlipCard,
  onAddCard,
  onToggleDeckVisibility,
  onCopyShareLink,
  onDeleteDeck,
  onOpenPractice,
  onNextCard,
  onReviewCard,
  onDeleteCard,
}) {
  return (
    <Paper elevation={0} sx={{ p: { xs: 2.5, md: 3, lg: 3.25 }, borderRadius: "14px", bgcolor: "var(--toeic-surface)", boxShadow: "var(--toeic-shadow-card)" }}>
      {selectedDeck ? (
        <>
          <DeckReviewHeader
            selectedDeck={selectedDeck}
            cards={cards}
            isMineDeck={isMineDeck}
            hasNextCard={hasNextCard}
            onAddCard={onAddCard}
            onToggleDeckVisibility={onToggleDeckVisibility}
            onCopyShareLink={onCopyShareLink}
            onDeleteDeck={onDeleteDeck}
            onOpenPractice={onOpenPractice}
            onNextCard={onNextCard}
          />

          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              mb: 3,
              height: 8,
              borderRadius: 999,
              bgcolor: "#edf7ff",
              "& .MuiLinearProgress-bar": { bgcolor: "#6d4aff", borderRadius: 999 },
            }}
          />

          <ActiveFlashcard
            activeCard={activeCard}
            isFlipped={isFlipped}
            onFlipCard={onFlipCard}
          />

          {activeCard && (
            <Box sx={{ mt: 2 }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                <Button
                  onClick={() => onReviewCard(activeCard)}
                  disabled={!isSignedIn}
                  variant={activeCard.remembered ? "contained" : "outlined"}
                  startIcon={<CheckCircleIcon />}
                  sx={{
                    borderRadius: "999px",
                    bgcolor: activeCard.remembered ? "#35c759" : undefined,
                    "&:hover": { bgcolor: activeCard.remembered ? "#2fb44f" : "#edf7ff" },
                  }}
                >
                  {activeCard.remembered ? "Remembered" : "Mark remembered"}
                </Button>
                {isMineDeck && (
                  <Button onClick={() => onDeleteCard(activeCard._id)} startIcon={<DeleteIcon />} sx={{ borderRadius: "999px", color: "#d81b60" }}>
                    Delete card
                  </Button>
                )}
              </Stack>
              {!isSignedIn && (
                <Typography sx={{ mt: 1, color: "var(--toeic-ink-soft)", fontSize: 13, fontWeight: 500 }}>
                  Sign in to save remembered cards to your account.
                </Typography>
              )}
            </Box>
          )}
        </>
      ) : (
        <Typography sx={{ color: "var(--toeic-ink-soft)" }}>
          Select a deck to start reviewing.
        </Typography>
      )}
    </Paper>
  );
}

function DeckReviewHeader({
  selectedDeck,
  cards,
  isMineDeck,
  hasNextCard,
  onAddCard,
  onToggleDeckVisibility,
  onCopyShareLink,
  onDeleteDeck,
  onOpenPractice,
  onNextCard,
}) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) auto", lg: "1fr" },
        gap: { xs: 1.5, sm: 2, lg: 1.25 },
        alignItems: "start",
        mb: { xs: 2, lg: 2.25 },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ color: "var(--toeic-ink-deep)", fontWeight: 700, fontSize: { xs: 24, lg: 28 }, lineHeight: 1.1 }}>
          {selectedDeck.name}
        </Typography>
        <Typography sx={{ color: "var(--toeic-ink-soft)", fontWeight: 400, maxWidth: { lg: 720 } }}>
          {selectedDeck.description || "Review this deck one card at a time."}
        </Typography>
      </Box>
      <Stack
        direction="row"
        spacing={0.75}
        sx={{
          justifySelf: { xs: "stretch", sm: "end" },
          justifyContent: { xs: "space-around", sm: "flex-end", lg: "flex-start" },
          p: { xs: 0.75, sm: 0 },
          borderRadius: { xs: "18px", sm: 0 },
          bgcolor: { xs: "#f3fbff", sm: "transparent" },
          border: { xs: "1px solid #d7eaf5", sm: "none" },
          overflowX: { xs: "auto", sm: "visible" },
          flexWrap: { xs: "nowrap", sm: "wrap" },
          rowGap: { sm: 0.75, lg: 1 },
          maxWidth: { sm: 680, lg: "100%" },
          "& .mobile-action": {
            minWidth: 48,
            display: { xs: "grid", sm: "none" },
            justifyItems: "center",
            gap: 0.25,
          },
          "& .mobile-action-label": {
            color: "var(--toeic-ink-soft)",
            fontSize: 10.5,
            fontWeight: 700,
            lineHeight: 1,
            whiteSpace: "nowrap",
          },
        }}
      >
        {isMineDeck && (
          <>
            <MobileAction title="Add card" label="Add" onClick={onAddCard} color="#6d4aff" bg="#f0ecff">
              <AddIcon />
            </MobileAction>
            <Button onClick={onAddCard} variant="contained" startIcon={<AddIcon />} sx={{ display: { xs: "none", sm: "inline-flex" }, borderRadius: "999px", bgcolor: "#6d4aff", "&:hover": { bgcolor: "#5635d6" } }}>
              Add card
            </Button>
            <MobileAction title={selectedDeck.isPublic ? "Unpublish deck" : "Publish deck"} label={selectedDeck.isPublic ? "Hide" : "Post"} onClick={onToggleDeckVisibility} color="var(--toeic-sky-deep)">
              <PublicIcon />
            </MobileAction>
            <Button onClick={onToggleDeckVisibility} startIcon={<PublicIcon />} sx={{ display: { xs: "none", sm: "inline-flex" }, borderRadius: "999px", px: { sm: 1.25, lg: 1.75 } }}>
              {selectedDeck.isPublic ? "Unpublish" : "Publish"}
            </Button>
            {selectedDeck.isPublic && (
              <>
                <MobileAction title="Copy share link" label="Share" onClick={onCopyShareLink} color="var(--toeic-sky-deep)">
                  <ContentCopyIcon />
                </MobileAction>
                <Button onClick={onCopyShareLink} startIcon={<ContentCopyIcon />} sx={{ display: { xs: "none", sm: "inline-flex" }, borderRadius: "999px", px: { sm: 1.25, lg: 1.75 } }}>
                  Share
                </Button>
              </>
            )}
            <MobileAction title="Delete deck" label="Delete" onClick={onDeleteDeck} color="#d81b60">
              <DeleteIcon />
            </MobileAction>
            <Button onClick={onDeleteDeck} startIcon={<DeleteIcon />} sx={{ display: { xs: "none", sm: "inline-flex" }, borderRadius: "999px", color: "#d81b60", px: { sm: 1.25, lg: 1.75 } }}>
              Delete deck
            </Button>
          </>
        )}
        <MobileAction title="Practice mode" label="Practice" onClick={onOpenPractice} disabled={!cards.length} color="var(--toeic-sky-deep)" bg="#fffefd">
          <CheckCircleIcon />
        </MobileAction>
        <Button
          onClick={onOpenPractice}
          disabled={!cards.length}
          variant="outlined"
          startIcon={<CheckCircleIcon />}
          sx={{ display: { xs: "none", sm: "inline-flex" }, borderRadius: "999px", borderColor: "#d7eaf5" }}
        >
          Practice
        </Button>
        <MobileAction title="Next card" label={hasNextCard ? "Next" : "Only 1"} onClick={onNextCard} disabled={!hasNextCard} color="var(--toeic-sky-deep)" bg="#fffefd">
          <ArrowForwardIcon />
        </MobileAction>
        <Button onClick={onNextCard} disabled={!hasNextCard} variant="outlined" endIcon={<ArrowForwardIcon />} sx={{ display: { xs: "none", sm: "inline-flex" }, borderRadius: "999px", borderColor: "#d7eaf5" }}>
          Next
        </Button>
      </Stack>
    </Box>
  );
}

function MobileAction({ title, label, onClick, disabled, color, bg, children }) {
  return (
    <Tooltip title={title}>
      <Box className="mobile-action">
        <IconButton onClick={onClick} disabled={disabled} sx={{ color, bgcolor: bg }}>
          {children}
        </IconButton>
        <Box component="span" className="mobile-action-label">
          {label}
        </Box>
      </Box>
    </Tooltip>
  );
}

function ActiveFlashcard({ activeCard, isFlipped, onFlipCard }) {
  if (!activeCard) {
    return (
      <Box sx={{ p: 4, borderRadius: "18px", bgcolor: "#faf7ff", textAlign: "center" }}>
        <Typography sx={{ color: "var(--toeic-ink-soft)" }}>
          This deck has no cards yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      onClick={onFlipCard}
      sx={{
        perspective: "1100px",
        height: { xs: 360, sm: 340, md: 380 },
        cursor: "pointer",
        outline: "none",
      }}
    >
      <Box
        sx={{
          position: "relative",
          height: "100%",
          transformStyle: "preserve-3d",
          transition: "transform 560ms cubic-bezier(.2,.72,.22,1)",
          transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
          "&:hover": {
            transform: isFlipped
              ? "translateY(-2px) rotateY(180deg)"
              : "translateY(-2px) rotateY(0deg)",
          },
        }}
      >
        <CardFace card={activeCard} />
        <CardBack card={activeCard} />
      </Box>
    </Box>
  );
}

function CardFace({ card }) {
  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        p: { xs: 3, md: 4 },
        borderRadius: "18px",
        border: "1.5px dashed #d7eaf5",
        bgcolor: "#faf7ff",
        backfaceVisibility: "hidden",
        display: "grid",
        placeItems: "center",
        textAlign: "center",
        overflow: "hidden",
        boxShadow: "0 18px 36px -32px rgba(19, 43, 63, 0.46)",
      }}
    >
      <Box>
        <Typography sx={{ color: "#6d4aff", fontSize: 13, fontWeight: 700, textTransform: "uppercase", mb: 1 }}>
          Word
        </Typography>
        <Typography sx={{ color: "var(--toeic-ink-deep)", fontSize: { xs: 34, md: 48 }, fontWeight: 700, lineHeight: 1.08 }}>
          {card.front}
        </Typography>
        {(card.ipa || card.audioUrl || card.audioUrls?.length) && (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mt: 1.5 }}>
            {card.ipa && (
              <Typography
                sx={{
                  color: "var(--toeic-ink-soft)",
                  fontFamily: "var(--lingualica-font-ipa)",
                  fontSize: 18,
                  fontWeight: 500,
                }}
              >
                {formatIPA(card.ipa)}
              </Typography>
            )}
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                playPronunciation(card.front, [
                  ...(card.audioUrls || []),
                  card.audioUrl,
                ]);
              }}
              sx={{ color: "#6d4aff", bgcolor: "#f0ecff" }}
            >
              <VolumeUpIcon fontSize="small" />
            </IconButton>
          </Stack>
        )}
        <Typography sx={{ mt: 3, color: "var(--toeic-ink-soft)", fontSize: 13 }}>
          Tap the card to flip
        </Typography>
      </Box>
    </Box>
  );
}

function CardBack({ card }) {
  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        p: { xs: 3, md: 4 },
        borderRadius: "18px",
        border: "1.5px dashed #b9e8cf",
        bgcolor: "#f7fff9",
        backfaceVisibility: "hidden",
        transform: "rotateY(180deg)",
        display: "grid",
        placeItems: "center",
        textAlign: "center",
        overflow: "hidden",
        boxShadow: "0 18px 36px -32px rgba(19, 43, 63, 0.46)",
      }}
    >
      <Box className="lingualica-scrollarea" sx={{ maxHeight: "100%", overflowY: "auto", px: 0.5 }}>
        <Typography sx={{ color: "#35a86a", fontSize: 13, fontWeight: 700, textTransform: "uppercase", mb: 1 }}>
          Meaning
        </Typography>
        <Typography sx={{ color: "var(--toeic-ink-deep)", fontSize: { xs: 24, md: 34 }, fontWeight: 700, lineHeight: 1.16 }}>
          {card.back}
        </Typography>
        {card.example && (
          <ExampleLines
            example={card.example}
            term={card.front}
            sx={{
              mt: 2,
              justifyItems: "center",
              "& .MuiTypography-root": {
                color: "var(--toeic-ink-soft)",
                fontSize: { xs: 14.5, md: 16 },
                lineHeight: 1.45,
              },
            }}
          />
        )}
        <Typography sx={{ mt: 3, color: "var(--toeic-ink-soft)", fontSize: 13 }}>
          Tap again to return
        </Typography>
      </Box>
    </Box>
  );
}

export default DeckReviewPanel;
